import proj4 from "proj4";
import type {
  LngLat,
  OsmBackgroundOptions,
  RenderingAmbiance,
  RenderingOptions,
  RenderingTint,
  OsmStyleName,
  Shape,
  ShapeBadge,
  ShapeStyle,
  ShapeTextStyle,
  ShapeImageStyle,
  ShapeThemes,
  TileLayerName,
} from "../../adapters/types";
import type { MapFraming } from "../../core/framing";
import { normalizeFraming } from "../../core/framing";
import { TEXT_FONTS } from "../../core/textFonts";

/**
 * Éléments communs à toutes les versions du format exemple.json (voir BESOINS.md §8.1) : le CRS
 * source, la boucle `layers`/`order`/vue, et les types version-agnostiques (`LoadedData`,
 * `ParseJsonOptions`...). Chaque connecteur de version (`./v1.ts`, `./v2.ts`) n'a plus qu'à
 * fournir sa propre conversion d'un feature isolé (`parseFeature`) — tout le reste (layers,
 * reprojection, ordre d'empilement, vue initiale) est mis en commun ici plutôt que dupliqué par
 * connecteur, pour qu'il ne puisse pas dériver silencieusement d'une version à l'autre.
 */

/** EPSG:3857 (Web Mercator) — le CRS utilisé par exemple.json, dans toutes ses versions connues. */
const WEB_MERCATOR =
  "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs";

export function mercatorToLngLat([x, y]: [number, number]): LngLat {
  const [lng, lat] = proj4(WEB_MERCATOR, "WGS84", [x, y]);
  return [lng, lat];
}

/** Sens inverse de `mercatorToLngLat` — voir `v1.ts`/`v2.ts`'s `shapeToRawFeature*`. */
export function lngLatToMercator([lng, lat]: LngLat): [number, number] {
  const [x, y] = proj4("WGS84", WEB_MERCATOR, [lng, lat]);
  return [x, y];
}

/** `configurations.rotation` est en radians, convention OpenLayers (voir `buildViewFromConfigurations`
 * ci-dessous) — converti en degrés au chargement, seule unité connue de `MapAdapter`/`LoadedView.rotationDeg`. */
export function rawRotationToDeg(rotation: number): number {
  return (rotation * 180) / Math.PI;
}

/** Sens inverse de `rawRotationToDeg` — pour reconstruire `configurations.rotation` à partir d'un
 * `LoadedView.rotationDeg`/`MapViewState.rotationDeg` quand on réécrit le document JSON (ex.
 * `TokaMapConfig.onSaveConfiguration`, voir BESOINS.md §7.1). Aucune inversion de signe : la
 * convention "boussole" de `MapAdapter` et la convention OpenLayers du fichier sont déjà traitées
 * comme équivalentes par `rawRotationToDeg`. */
export function rotationDegToRawRotation(rotationDeg: number): number {
  return (rotationDeg * Math.PI) / 180;
}

interface RawLayer<TFeature> {
  type: string;
  features: TFeature[];
}

export interface RawConfigurations {
  /**
   * Version du format du document (voir BESOINS.md §8.1) — portée par `configurations` plutôt que
   * par la racine du document depuis que ce champ existe : `configurations` est le seul endroit du
   * format qui décrit "comment lire ce document" plutôt que son contenu métier. Absent : traité
   * comme `1` (voir `jsonLoader.ts`, le seul endroit qui lit ce champ).
   */
  formatVersion?: number;
  center?: [number, number];
  zoom?: number;
  zoomMin?: number;
  zoomMax?: number;
  rotation?: number;
  /**
   * Fond de carte (voir `TileLayerName`) — même nom de champ en v1 et v2 (contrairement à
   * `featureStyle`/`defaultShapeStyle`), donc porté directement ici plutôt que par un connecteur de
   * version. Typé `string` (pas `TileLayerName`) : c'est du JSON non validé, une valeur inconnue est
   * ramenée à `"unset"` par `normalizeTileLayerName` plutôt que de faire échouer le chargement.
   */
  tileLayerName?: string;
  /** Même nom de champ en v1 et v2, porté directement ici comme `tileLayerName` ci-dessus. Typé
   * `unknown` : du JSON non validé, ramené champ par champ par `normalizeOsmOptions`. */
  osmOptions?: unknown;
  /** Porté directement ici comme `osmOptions`, ramené champ par champ par `normalizeRenderingOptions`. */
  renderingOptions?: unknown;
  /**
   * Couleur de fond du conteneur DOM de la carte (voir `MapAdapter.getBackgroundColor`) — même nom de
   * champ en v1 et v2, porté directement ici comme `tileLayerName` ci-dessus. Absent : traité comme
   * `undefined` (voir `LoadedView.backgroundColor`), même convention que `defaultShapeStyle` — laisse
   * `TokaMapConfig.defaultBackgroundColor` (valeur de construction) prendre le relais tant qu'aucune
   * valeur n'a encore été chargée/enregistrée.
   */
  backgroundColor?: string;
  /**
   * Active (ou non) le repérage de la position de l'utilisateur (voir BESOINS.md §15) — même nom de
   * champ en v1 et v2, porté directement ici comme `tileLayerName` ci-dessus. Absent : traité comme
   * `undefined` (voir `LoadedView.geolocation`/`TokaMapConfig.geolocation`), pas comme `false` — laisse
   * `TokaMapConfig.geolocation` (valeur de construction) prendre le relais tant qu'aucune valeur n'a
   * encore été chargée.
   */
  geolocation?: boolean;
  /**
   * Autorise (ou non) l'inclinaison 3D de la caméra (voir `MapAdapter.getAllowPitch`) — même nom de
   * champ en v1 et v2, porté directement ici comme `tileLayerName` ci-dessus. Absent : traité comme
   * `undefined` (voir `LoadedView.allowPitch`/`TokaMapConfig.defaultAllowPitch`), pas comme `false` —
   * laisse `TokaMapConfig.defaultAllowPitch` (valeur de construction) prendre le relais tant qu'aucune
   * valeur n'a encore été chargée.
   */
  allowPitch?: boolean;
  framing?: unknown;
}

/**
 * Ramène une valeur brute de `configurations.tileLayerName` à une `TileLayerName` valide — `undefined`
 * (champ absent du JSON) reste `undefined`, même convention que `defaultShapeStyle` : laisse
 * `TokaMapConfig.defaultTileLayerName` (valeur de construction) prendre le relais tant qu'aucune
 * valeur n'a encore été chargée/enregistrée (voir `createTokaMap`). Une valeur PRÉSENTE mais inconnue
 * (future version du format, donnée corrompue) retombe sur `"unset"` plutôt que de faire échouer le
 * chargement.
 */
export function normalizeTileLayerName(value: string | undefined): TileLayerName | undefined {
  if (value === undefined) return undefined;
  return value === "osm" || value === "satellite" ? value : "unset";
}

const OSM_STYLE_NAMES: readonly OsmStyleName[] = ["light", "dark", "sober", "detailed"];

/**
 * Même convention que `normalizeTileLayerName` : `undefined` reste `undefined`, et une valeur
 * présente mais mal formée est ignorée champ par champ plutôt que de faire échouer le chargement.
 */
export function normalizeOsmOptions(value: unknown): OsmBackgroundOptions | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};

  const raw = value as Record<string, unknown>;
  const options: OsmBackgroundOptions = {};

  if (typeof raw.style === "string" && (OSM_STYLE_NAMES as readonly string[]).includes(raw.style)) {
    options.style = raw.style as OsmStyleName;
  }
  // Toute chaîne non vide : le suffixe `name:<langue>` d'OSM n'a pas de liste close. `null` reste
  // significatif (garder le nom local), voir `OsmBackgroundOptions.language`.
  if (typeof raw.language === "string" && raw.language !== "") options.language = raw.language;
  if (raw.language === null) options.language = null;

  for (const key of ["placeLabels", "roads", "water", "vegetation", "roadLabels", "poiLabels", "buildings", "buildings3d"] as const) {
    if (typeof raw[key] === "boolean") options[key] = raw[key] as boolean;
  }
  if (typeof raw.fade === "number" && Number.isFinite(raw.fade)) options.fade = raw.fade;

  return options;
}

const RENDERING_AMBIANCE_NAMES: readonly RenderingAmbiance[] = ["soft", "noon", "evening", "night", "contrast", "custom"];
const RENDERING_TINT_NAMES: readonly RenderingTint[] = ["neutral", "cool", "warm", "night"];

/** Même convention que `normalizeOsmOptions`. */
export function normalizeRenderingOptions(value: unknown): RenderingOptions | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};

  const raw = value as Record<string, unknown>;
  const options: RenderingOptions = {};
  if (typeof raw.ambiance === "string" && (RENDERING_AMBIANCE_NAMES as readonly string[]).includes(raw.ambiance)) {
    options.ambiance = raw.ambiance as RenderingAmbiance;
  }
  if (typeof raw.tint === "string" && (RENDERING_TINT_NAMES as readonly string[]).includes(raw.tint)) options.tint = raw.tint as RenderingTint;
  for (const key of ["contactShadows", "decorShadows", "arrivalFlight", "spotlight"] as const) {
    if (typeof raw[key] === "boolean") options[key] = raw[key] as boolean;
  }
  for (const key of ["contactShadowStrength", "sunAzimuth", "tintStrength", "lightIntensity", "lightAngle"] as const) {
    if (typeof raw[key] === "number" && Number.isFinite(raw[key])) options[key] = raw[key] as number;
  }
  return options;
}

/**
 * Document racine, générique sur le type de feature (`TFeature` = `RawFeature` en v1, `RawFeatureV2`
 * en v2) ET sur la forme de `configurations` (`TConfigurations`, voir `RawConfigurations` — chaque
 * version y ajoute ses propres champs, ex. `featureStyle` en v1 vs `defaultShapeStyle` en v2) :
 * `layers`/`extras` restent eux identiques quelle que soit la version (voir BESOINS.md §8.1).
 */
export interface RawDocument<TFeature, TConfigurations extends RawConfigurations = RawConfigurations> {
  layers: Record<string, RawLayer<TFeature>>;
  configurations?: TConfigurations;
  extras?: unknown;
}

/**
 * Style par défaut lu depuis `configurations` (voir BESOINS.md §3/§8.2) — chaque connecteur de
 * version l'extrait à sa façon (nom de champ différent, voir `v1.ts`/`v2.ts`'s
 * `extractDefaultShapeStyles`) mais produit toujours cette même forme version-agnostique.
 * `defaultShapeStyle` alimente `TokaMapInstance.setDefaultShapeStyle`/`MapAdapter.setDefaultShapeStyle`
 * (style des nouvelles formes dessinées). Absent : la carte démarre avec la valeur de
 * `TokaMapConfig.defaultShapeTheme` (ou son propre repli), sans que cette option ne soit écrasée par
 * le chargement. Pas de notion de style de survol par défaut ici (`Shape.hoverStyle` reste un champ
 * par-forme uniquement, voir `adapters/types.ts`).
 */
export interface DefaultShapeStyles {
  defaultShapeStyle?: ShapeStyle;
}

export interface LoadedView extends DefaultShapeStyles {
  /**
   * Thèmes embarqués dans le document (`configurations.themes` en v2 — v1 est gelé et n'en a pas),
   * alimente `MapAdapter.setThemes` une fois le chargement terminé. C'est ce qui permet de résoudre
   * les `Shape.themeId` d'une carte publiée sans connaître la source des thèmes. Absent : la table
   * passée à la construction (`TokaMapConfig.themes`) n'est pas écrasée par le chargement.
   */
  themes?: ShapeThemes;
  center?: LngLat;
  zoom?: number;
  zoomMin?: number;
  zoomMax?: number;
  rotationDeg?: number;
  /** Voir `TileLayerName`/`normalizeTileLayerName`. */
  tileLayerName?: TileLayerName;
  /** Voir `OsmBackgroundOptions`/`normalizeOsmOptions`. */
  osmOptions?: OsmBackgroundOptions;
  /** Voir `RenderingOptions`/`normalizeRenderingOptions`. */
  renderingOptions?: RenderingOptions;
  /** Voir `RawConfigurations.backgroundColor`. */
  backgroundColor?: string;
  /** Voir `RawConfigurations.geolocation`/BESOINS.md §15. */
  geolocation?: boolean;
  /** Voir `RawConfigurations.allowPitch`. */
  allowPitch?: boolean;
  framing?: MapFraming;
}

export interface LoadedData {
  shapes: Shape[];
  view?: LoadedView;
  /**
   * Version du format effectivement utilisée pour parser le document (voir BESOINS.md §8.1) —
   * attachée par `jsonLoader.ts`'s `parseJsonDocument`, pas par les connecteurs
   * eux-mêmes (qui n'ont pas besoin de la connaître). Absente si les formes viennent d'un
   * `TokaMapConfig.loadInitialData` personnalisé (pas de notion de version JSON dans ce cas) —
   * `createTokaMap`'s passerelle de migration ne s'active alors jamais, voir `FormatMigrationGate`.
   */
  formatVersion?: number;
}

/**
 * Vide pour l'instant (l'ancienne `includeEntityImages` a été retirée — l'extraction depuis
 * `entity` est désormais toujours tentée au chargement v1, filtrable à l'affichage via
 * `includeIndirectImages`, voir `v1.ts`'s `resolveV1Image`) — gardée comme point d'extension pour
 * une future option de chargement commune aux connecteurs.
 */
export interface ParseJsonOptions {}

export interface ParseJsonFeatureOptions extends ParseJsonOptions {
  /** Voir `Shape.layer`/`Shape.order`. Omis par défaut (voir leurs docs sur `Shape`). */
  layer?: number;
  order?: number;
}

/**
 * Un connecteur par version de format — même vocabulaire que BESOINS.md §10 ("connecteur
 * cartographique", qui découple la logique métier de la librairie de carte sous-jacente) : un
 * connecteur JSON découple `jsonLoader.ts` de la version exacte du format qu'il lit. Voir le
 * registre dans `jsonLoader.ts`.
 */
export interface JsonFormatConnector {
  readonly version: number;
  parseDocument(raw: unknown, options: ParseJsonOptions): LoadedData;
}

function buildViewFromConfigurations<TConfigurations extends RawConfigurations>(
  configurations: TConfigurations | undefined,
  extractDefaultShapeStyles: (configurations: TConfigurations | undefined) => Partial<LoadedView>,
): LoadedView | undefined {
  if (!configurations) return undefined;
  return {
    center: configurations.center ? mercatorToLngLat(configurations.center) : undefined,
    zoom: configurations.zoom,
    zoomMin: configurations.zoomMin,
    zoomMax: configurations.zoomMax,
    rotationDeg: configurations.rotation != null ? rawRotationToDeg(configurations.rotation) : undefined,
    tileLayerName: normalizeTileLayerName(configurations.tileLayerName),
    osmOptions: normalizeOsmOptions(configurations.osmOptions),
    renderingOptions: normalizeRenderingOptions(configurations.renderingOptions),
    backgroundColor: configurations.backgroundColor,
    geolocation: configurations.geolocation,
    allowPitch: configurations.allowPitch,
    framing: normalizeFraming(configurations.framing),
    ...extractDefaultShapeStyles(configurations),
  };
}

/**
 * Boucle `layers`/`order`/vue, identique pour toutes les versions du format (voir `RawDocument`) —
 * seule la conversion d'un feature isolé (`parseFeature`) ET l'extraction des styles par défaut
 * depuis `configurations` (`extractDefaultShapeStyles`, voir `DefaultShapeStyles`), toutes deux
 * fournies par chaque connecteur, varient.
 */
export function parseLayeredDocument<TFeature, TConfigurations extends RawConfigurations = RawConfigurations>(
  raw: RawDocument<TFeature, TConfigurations> | undefined,
  options: ParseJsonOptions,
  parseFeature: (feature: TFeature, featureOptions: ParseJsonFeatureOptions) => Shape | null,
  extractDefaultShapeStyles: (configurations: TConfigurations | undefined) => Partial<LoadedView>,
): LoadedData {
  const shapes: Shape[] = [];
  // Compteur global d'apparition dans le fichier, tous calques confondus : sert de Shape.order
  // (départage stable entre formes du même calque, voir ShapeStore.getAll).
  let order = 0;
  for (const [layerKey, layer] of Object.entries(raw?.layers ?? {})) {
    // Détermine l'ordre d'empilement (voir Shape.layer) : une clé non numérique (format
    // théoriquement libre) ne bloque pas le chargement, la forme reste juste sans calque.
    const parsedLayerKey = Number(layerKey);
    const shapeLayer = Number.isFinite(parsedLayerKey) ? parsedLayerKey : undefined;

    for (const feature of layer.features ?? []) {
      const shape = parseFeature(feature, { ...options, layer: shapeLayer, order });
      if (!shape) continue;
      shapes.push(shape);
      order++;
    }
  }

  return { shapes, view: buildViewFromConfigurations(raw?.configurations, extractDefaultShapeStyles) };
}

/**
 * Reconvertit `Shape.id` (toujours une chaîne en interne) en `number` quand il est entièrement
 * numérique — `properties.id` est un nombre dans le fichier réel, donc un id issu du chargement
 * (`String(properties.id)`) doit repartir tel quel au lieu de rester une chaîne au premier `onSave`.
 * Un id non numérique (ex. forme dessinée à la main, `generateId()`) reste tel quel. Identique pour
 * toutes les versions (ne touche à aucun champ image).
 */
export function extractRawId(shape: Shape): number | string {
  return shape.id !== "" && !Number.isNaN(Number(shape.id)) ? Number(shape.id) : shape.id;
}

/**
 * Thème tel que la racine d'un document de PROJET le porte : la ressource d'API telle quelle, dont
 * seule `configuration` intéresse le rendu.
 */
interface RawProjectTheme {
  id?: unknown;
  configuration?: {
    appearance?: { normal?: ShapeStyle; hover?: ShapeStyle };
    text?: { normal?: unknown; hover?: unknown };
    image?: unknown;
    height?: unknown;
    elevation?: unknown;
    hoverHeight?: unknown;
    hoverElevation?: unknown;
    minZoomOffset?: unknown;
    volumeOpacity?: unknown;
    badge?: unknown;
  };
}

/**
 * Table des thèmes du core depuis le tableau `themes` que porte la RACINE d'un document de projet.
 *
 * Les thèmes appartiennent au projet et non à une carte : le document d'une carte n'en porte aucune
 * table, et c'est au consommateur de redescendre celle-ci pour que les `properties.theme` des formes
 * se résolvent (voir `RawFeaturePropertiesV2.theme`).
 *
 * Tolérant par nécessité : le producteur du document rend ses objets vides en `[]` et non `{}`, et
 * un thème sans id n'est référençable par personne. Tout écart donne une table vide plutôt qu'une
 * exception — un thème illisible doit laisser la carte s'afficher, les formes rendant alors leur
 * style propre.
 */
export function parseProjectThemes(raw: unknown): ShapeThemes {
  if (!Array.isArray(raw)) return {};

  const entries = raw.flatMap((entry: unknown) => {
    const theme = entry as RawProjectTheme | null;
    const id = theme?.id;

    if (id === undefined || id === null || id === "") return [];

    const appearance = theme?.configuration?.appearance;
    const text = theme?.configuration?.text;
    const height = theme?.configuration?.height;
    const elevation = theme?.configuration?.elevation;
    const hoverHeight = theme?.configuration?.hoverHeight;
    const hoverElevation = theme?.configuration?.hoverElevation;
    const minZoomOffset = theme?.configuration?.minZoomOffset;
    const volumeOpacity = theme?.configuration?.volumeOpacity;
    const badge = themeBadge(theme?.configuration?.badge);

    return [[String(id), {
      style: orUndefined(appearance?.normal),
      hoverStyle: orUndefined(appearance?.hover),
      text: orUndefined(themeTextStyle(text?.normal)),
      hoverText: orUndefined(themeTextStyle(text?.hover)),
      image: orUndefined(themeImageStyle(theme?.configuration?.image)),
      ...(typeof height === "number" && height >= 0 ? { height } : {}),
      ...(typeof elevation === "number" && elevation >= 0 ? { elevation } : {}),
      ...(typeof hoverHeight === "number" && hoverHeight >= 0 ? { hoverHeight } : {}),
      ...(typeof hoverElevation === "number" && hoverElevation >= 0 ? { hoverElevation } : {}),
      ...(typeof minZoomOffset === "number" && minZoomOffset >= 0 ? { minZoomOffset } : {}),
      ...(typeof volumeOpacity === "number" && volumeOpacity >= 0 && volumeOpacity <= 1 ? { volumeOpacity } : {}),
      ...(badge !== undefined ? { badge } : {}),
    }] as const];
  });

  return Object.fromEntries(entries);
}

/** Le marqueur d'un thème, sans les `null` que l'écriture en `merge-patch+json` y laisse. */
function themeBadge(raw: unknown): ShapeBadge | false | undefined {
  if (raw === false) return false;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  return Object.fromEntries(Object.entries(raw).filter(([, value]) => value !== null && value !== undefined)) as ShapeBadge;
}

type FieldReader = (value: unknown) => unknown;

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const color: FieldReader = (value) => (typeof value === "string" && value ? value : undefined);
const number: FieldReader = (value) => (isNumber(value) ? value : undefined);
const positive: FieldReader = (value) => (isNumber(value) && value > 0 ? value : undefined);
const share: FieldReader = (value) => (isNumber(value) && value >= 0 && value <= 1 ? value : undefined);
const flag: FieldReader = (value) => (typeof value === "boolean" ? value : undefined);
const oneOf = (...allowed: string[]): FieldReader => (value) => (typeof value === "string" && allowed.includes(value) ? value : undefined);

const text: FieldReader = (value) => (typeof value === "string" && value.trim() !== "" ? value : undefined);

const THEME_TEXT_READERS: Record<keyof ShapeTextStyle, FieldReader> = {
  text,
  color,
  bgColor: color,
  fixedSizePx: positive,
  size: positive,
  font: oneOf(...TEXT_FONTS.map((font) => font.id)),
  fontStyle: oneOf("regular", "bold", "italic"),
  uppercase: flag,
  opacity: share,
  justify: oneOf("left", "right"),
  horizontalAlign: share,
  verticalAlign: share,
  rotation: number,
  rotateWithMap: flag,
  keepReadable: flag,
};

const THEME_IMAGE_READERS: Record<keyof ShapeImageStyle, FieldReader> = {
  src: text,
  scale: positive,
  radius: share,
  opacity: share,
  rotation: number,
  rotateWithMap: flag,
  horizontalAlign: share,
  verticalAlign: share,
  minScreenPx: (value) => (isNumber(value) && value >= 0 ? value : undefined),
};

/** Les champs reconnus et valides d'un objet de configuration ; les `null` de l'écriture en `merge-patch+json` et tout le reste tombent. */
function readFields<T extends object>(raw: unknown, readers: Record<keyof T, FieldReader>): T | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;

  const entries = Object.entries(readers as Record<string, FieldReader>)
    .map(([key, read]) => [key, read((raw as Record<string, unknown>)[key])] as const)
    .filter(([, value]) => value !== undefined);

  return Object.fromEntries(entries) as T;
}

function themeTextStyle(raw: unknown): ShapeTextStyle | undefined {
  return readFields<ShapeTextStyle>(raw, THEME_TEXT_READERS);
}

function themeImageStyle(raw: unknown): ShapeImageStyle | undefined {
  return readFields<ShapeImageStyle>(raw, THEME_IMAGE_READERS);
}

/**
 * Redescend la table des thèmes du PROJET dans le document d'une carte, à l'endroit où les
 * connecteurs de format la lisent (`configurations.themes`).
 *
 * Sans elle, une forme portant `properties.theme` rendrait son style propre : un thème inconnu est
 * ignoré. Le document est rendu tel quel quand il n'y a aucun thème, pour ne pas lui inventer une
 * `configurations` qu'il n'avait pas.
 */
export function withProjectThemes(document: unknown, themes: ShapeThemes): unknown {
  if (!document || typeof document !== "object" || !Object.keys(themes).length) return document;

  const { configurations } = document as { configurations?: Record<string, unknown> };

  return { ...document, configurations: { ...configurations, themes } };
}

/**
 * `{}` (ou `[]`, que le producteur rend pour un objet vide) devient `undefined` : le core distingue
 * les deux — voir `ShapeTheme.hoverText`, où ABSENT signifie "hérite de l'état normal" alors qu'un
 * objet PRÉSENT sans couleur fait retomber le rendu sur les défauts de l'adapter.
 */
function orUndefined<T extends object>(value: T | undefined): T | undefined {
  if (!value || Array.isArray(value)) return undefined;

  return Object.values(value).some((v) => v !== undefined) ? value : undefined;
}
